import { RootStateOrAny, useSelector } from "react-redux";
import ReactGA from "react-ga4";
import { useMemo } from "react";
import { User } from "types";

const useCurrentUser = () => {
  const userRaw = useSelector((state: RootStateOrAny) => state.auth?.user);

  if (userRaw) {
    ReactGA.set({
      title: "user_properties",
      user_id: userRaw.userId,
      user_type: userRaw.type,
      user_isVerified: userRaw.verified,
    });
  }

  const isAuthenticated = useMemo(() => {
    if (!userRaw) {
      return false;
    }
    const currentTime = new Date().getTime() / 1000;
    return currentTime < userRaw.expiresIn;
  }, [userRaw]);

  const user: User | null = useMemo(() => {
    if (!userRaw) {
      return null;
    }

    const activeOrg = userRaw.activeOrganization;

    return {
      fullName: `${userRaw.firstName} ${userRaw.lastName}`,
      firstName: userRaw.firstName,
      lastName: userRaw.lastName,
      email: userRaw.email,
      type: userRaw.type,
      id: userRaw.userId,
      createdAt: userRaw.createdAt,
      defaultLanguage: userRaw.defaultLanguage,
      digestSubscription: userRaw.digestSubscription,
      is2FAEnabled: userRaw.is2FAEnabled,
      slug: userRaw.slug,
      defaultReminder: userRaw.defaultReminder,
      notificationTimes: userRaw.notificationTimes,
      daysOfWeek: userRaw.daysOfWeek,
      notificationMessage: userRaw.notificationMessage,
      color: userRaw.color,
      icon: userRaw.icon,
      alias: userRaw.alias,
      activeOrganizationId: userRaw.activeOrganizationId,
      organizations: userRaw.organizations,
      activeOrganization: activeOrg,
      verified: userRaw.verified,
      removedPracticeId: userRaw.removedPracticeId,
      pinCode: userRaw.pinCode,
      pinVerified: userRaw.pinVerified,
      stripeCustomerId: userRaw.stripeCustomerId,
      role: userRaw.role,
    };
  }, [userRaw]);

  return {
    isVerified: isAuthenticated && userRaw?.verified === true,
    isSubscribed:
      userRaw?.isSubscribed === "true" || userRaw?.isSubscribed === true,
    isAuthenticated,
    isPatient: userRaw?.type === "patient",
    isTherapist: userRaw?.type === "therapist",
    isTherapistProfileComplete: userRaw?.isTherapistProfileComplete,
    isTemplateMaster:
      userRaw?.userId === process.env.REACT_APP_TEMPLATE_MASTER_ID,
    user,
  };
};

// Utility function to get plan limits from active organization
export const useActiveOrganizationPlanLimits = () => {
  const { user } = useCurrentUser();

  // Get plan limits from active organization's subscription
  const activeOrganizationPlan = user?.activeOrganization?.subscription?.plan;

  return {
    patientLimit: activeOrganizationPlan?.patientLimit || 0,
    groupLimit: activeOrganizationPlan?.groupLimit || 0,
    fileLimit: activeOrganizationPlan?.fileLimit || 0,
    customResourceLimit: activeOrganizationPlan?.customResourceLimit || 0,
    customAssignmentLimit: activeOrganizationPlan?.customAssignmentLimit || 0,
    assignmentLimit: activeOrganizationPlan?.assignmentLimit || 0,
    seatLimit: activeOrganizationPlan?.seat_limit || 0,
    practiceLimit: activeOrganizationPlan?.practice_limit || 0,
  };
};

export default useCurrentUser;
